import { useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import SiteFooter from "./components/SiteFooter";
import SiteHeader from "./components/SiteHeader";
import {
  DASHBOARD_SIGNUP_URL,
  IOS_APP_DESTINATION_URL,
} from "./data/platform-links";
import contentMap from "./data/site-content.json";
import socialPreview from "./data/social-preview.json";
import routeSocialPreviews from "./data/route-social-previews.json";
import MappedPage from "./pages/MappedPage";
import MedicalPage from "./pages/MedicalPage";
import PressPage from "./pages/PressPage";
import PricingPage from "./pages/PricingPage";
import CreatorsPage from "./pages/CreatorsPage";

const customPageMetadata = {
  "/": {
    path: "/",
    title: "10k Science",
    description:
      "Interactive 3D science experiences for iOS, web, and Meta Quest, built in collaboration with scientists and educators from top research institutions.",
    canonicalUrl: "https://10k.science",
    ogImage: socialPreview.url,
    twitterImage: socialPreview.url,
  },
  "/home": {
    path: "/home",
    title: "10k Science",
    description:
      "Interactive 3D science experiences for iOS, web, and Meta Quest, built in collaboration with scientists and educators from top research institutions.",
    canonicalUrl: "https://10k.science/home",
    ogImage: socialPreview.url,
    twitterImage: socialPreview.url,
  },
  "/medical": {
    path: "/medical",
    title: "Intelligent 3D Medical Experiences — 10k Science",
    description:
      "Explore AI-guided 3D medical experiences that make anatomy, physiology, and therapeutic mechanisms tangible.",
    canonicalUrl: "https://10k.science/medical",
    ogImage:
      "https://10k.science/assets/medical/anatomy-hero-1920.webp",
  },
  "/press": {
    path: "/press",
    title: "Press Assets — 10k Science",
    description:
      "High-resolution 10k Science screenshots, product images, and visual assets for press use.",
    canonicalUrl: "https://10k.science/press",
    ogImage:
      "https://10k.science/assets/medical/ai-guide-1600.webp",
  },
};

const findPage = (pathName) => {
  const contentPage = contentMap.pages.find((page) => page.path === pathName);
  return customPageMetadata[pathName]
    ? { ...contentPage, ...customPageMetadata[pathName] }
    : contentPage;
};

const isIosDevice = () =>
  /iPad|iPhone|iPod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" &&
    window.navigator.maxTouchPoints > 1);

const isExternalUrl = (url = "") => /^https?:/i.test(url);

function GetItRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    if (isIosDevice()) {
      if (isExternalUrl(IOS_APP_DESTINATION_URL)) {
        window.location.replace(IOS_APP_DESTINATION_URL);
      } else {
        navigate(IOS_APP_DESTINATION_URL, { replace: true });
      }
      return;
    }

    window.location.replace(DASHBOARD_SIGNUP_URL);
  }, [navigate]);

  return (
    <p className="visually-hidden">
      Redirecting to <a href={IOS_APP_DESTINATION_URL}>10k Science iOS app</a>
    </p>
  );
}

function PageRouter() {
  const location = useLocation();
  const normalizedPath =
    location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");
  const page = findPage(normalizedPath);

  useEffect(() => {
    if (process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
    const title = page?.title || "10k Science";
    const description = page?.description || "";
    document.title = title;

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        const [name, attributeValue] = attribute;
        element.setAttribute(name, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };
    const setOptionalMeta = (selector, attribute, value) => {
      const existing = document.head.querySelector(selector);
      if (!value) {
        existing?.remove();
        return;
      }
      setMeta(selector, attribute, value);
    };
    const canonicalUrl =
      page?.canonicalUrl !== undefined
        ? page.canonicalUrl
        : page
          ? `https://10k.science${page.path === "/" ? "" : page.path}`
          : "";
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    } else {
      canonical?.remove();
    }

    setMeta('meta[name="description"]', ["name", "description"], description);
    setMeta('meta[property="og:title"]', ["property", "og:title"], title);
    setMeta(
      'meta[property="og:description"]',
      ["property", "og:description"],
      description,
    );
    setOptionalMeta(
      'meta[property="og:url"]',
      ["property", "og:url"],
      canonicalUrl,
    );
    const routePreview = routeSocialPreviews[normalizedPath];
    const image = routePreview?.url || page?.ogImage || socialPreview.url;
    const twitterImage = routePreview?.url || page?.twitterImage || image;
    const imageDetails = [routePreview, socialPreview].find(
      (preview) => preview?.url === image,
    );
    setMeta(
      'meta[property="og:image"]',
      ["property", "og:image"],
      image,
    );
    // Keep image details paired with their artwork, including after navigation.
    for (const field of ["width", "height", "type", "alt"]) {
      setOptionalMeta(
        `meta[property="og:image:${field}"]`,
        ["property", `og:image:${field}`],
        imageDetails?.[field] || "",
      );
    }
    setMeta(
      'meta[name="twitter:card"]',
      ["name", "twitter:card"],
      "summary_large_image",
    );
    setOptionalMeta(
      'meta[name="twitter:title"]',
      ["name", "twitter:title"],
      title,
    );
    setOptionalMeta(
      'meta[name="twitter:description"]',
      ["name", "twitter:description"],
      description,
    );
    setOptionalMeta(
      'meta[name="twitter:url"]',
      ["name", "twitter:url"],
      canonicalUrl,
    );
    setOptionalMeta(
      'meta[name="twitter:image"]',
      ["name", "twitter:image"],
      twitterImage,
    );
    setOptionalMeta(
      'meta[name="twitter:image:alt"]',
      ["name", "twitter:image:alt"],
      [routePreview, socialPreview].find(
        (preview) => preview?.url === twitterImage,
      )?.alt || "",
    );
    setOptionalMeta(
      'meta[name="robots"]',
      ["name", "robots"],
      page?.robots,
    );
  }, [normalizedPath, page]);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#page">
        Skip to Content
      </a>
      <SiteHeader />
      <main className="site-main" id="page">
        <Routes>
          <Route
            element={<MappedPage page={findPage("/")} />}
            path="/"
          />
          <Route
            element={<MappedPage page={findPage("/home")} />}
            path="/home"
          />
          <Route
            element={<MappedPage page={findPage("/stories")} />}
            path="/stories"
          />
          <Route
            element={<MappedPage page={findPage("/contact")} />}
            path="/contact"
          />
          <Route element={<MedicalPage />} path="/medical" />
          <Route element={<PressPage />} path="/press" />
          <Route element={<PricingPage />} path="/pricing" />
          <Route element={<CreatorsPage />} path="/creators" />
          <Route
            element={<GetItRedirect />}
            path="/get-it"
          />
          <Route element={<MappedPage page={page} />} path="*" />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter
      future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
    >
      <PageRouter />
    </BrowserRouter>
  );
}
